import React, { useMemo } from 'react';
import { Box, Grid, Typography, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody, TextField, FormControl, InputLabel, Select, MenuItem, Button, Checkbox, Paper, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import { getRequest, postRequest, patchRequest } from 'Includes/api/apicall';
import { GET_URL, POST_URL, PATCH_URL } from 'Includes/urls';
import _ from 'lodash';
import Swal from 'sweetalert2';
import SummaryModal from 'Components/SummaryModal';
import { MAX_DATE_STR } from 'Constants/dateConstants';
import { VisibilityOutlined, EditOutlined, DownloadOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  ID_CARD_CONFIG_PREFIX,
  parseIdCardConfigFromNotes,
  listIdCardConfigLineItems,
} from 'Containers/Billing/idCardServiceConfig';
import {
  BIOMETRIC_CONFIG_PREFIX,
  parseBiometricConfigFromNotes,
  biometricKindLabel,
} from 'Containers/Billing/biometricServiceConfig';

function plusMonthsISO(dateStr, months) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1 + months, d);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

const DEFAULT_OTHER_SERVICE_GST_PERCENT = 18;

function CompanyIssueInvoice(props) {
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState({});
  const [selectedServices, setSelectedServices] = React.useState({});
  const [summaryOpen, setSummaryOpen] = React.useState(false)
  const [summaryData, setSummaryData] = React.useState(null)
  const [pendingPayload, setPendingPayload] = React.useState(null)
  const [submitDisable, setSubmitDisable] = React.useState(false)
  const [postSaveRedirect, setPostSaveRedirect] = React.useState('list')
  const [invoiceDetails, setInvoiceDetails] = React.useState({
    issue_date: '',
    due_date: '',
    status: 'DRAFT',
    notes: '',
  });
  const [invoiceErrors, setInvoiceErrors] = React.useState({});
  const [serviceErrors, setServiceErrors] = React.useState({});
  const [lineItemsLocked, setLineItemsLocked] = React.useState(false);
  const [selectedTermName, setSelectedTermName] = React.useState('');
  const [serviceTermSelection, setServiceTermSelection] = React.useState({});
  const [serviceGstSelection, setServiceGstSelection] = React.useState({});
  const [qtyUnitDialog, setQtyUnitDialog] = React.useState({
    open: false,
    idx: null,
    item_count: '',
    service_amount: '',
    isErp: false,
    isErpFixed: false,
    errors: {},
  });

  /** Edit form: Draft/Issued before GST invoice; Invoiced is read-only once GST invoice exists. */
  const STATUS_CHOICES = ['DRAFT', 'ISSUED'];
  const isInvoicedStatus = () => String(invoiceDetails.status || '').toUpperCase() === 'INVOICED';
  const statusLabel = (s) =>
    ({
      DRAFT: 'Draft',
      ISSUED: 'Issued',
      INVOICED: 'Invoiced',
      PARTIAL_PAID: 'Partial paid',
      PAID: 'Paid',
    }[s] || s);

  const isEditRoute = () => Boolean(new URLSearchParams(window.location.search).get('issue_invoice_id'));

  const formatAmount = (v) => {
    const n = parseFloat(v || 0);
    return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const isConfigStoredNotes = (notes) => {
    const n = String(notes || '');
    return n.startsWith(ID_CARD_CONFIG_PREFIX) || n.startsWith(BIOMETRIC_CONFIG_PREFIX);
  };

  const isIdCardServiceRow = (svc) => {
    const notes = String(svc?.notes || '');
    if (notes.startsWith(ID_CARD_CONFIG_PREFIX) || parseIdCardConfigFromNotes(notes)) return true;
    const serviceName = String(svc?.service_name || '').toLowerCase();
    const planName = String(svc?.plan_name || '').toLowerCase();
    const billingName = String(svc?.billing_name || '').toLowerCase();
    const notesLower = notes.toLowerCase();
    const blob = `${serviceName} ${planName} ${billingName}`;
    return (
      notesLower.includes('auto id card order from agreement') ||
      blob.includes('id card') ||
      blob.includes('idcard')
    );
  };

  const isBiometricServiceRow = (svc) => {
    const notes = String(svc?.notes || '');
    if (notes.startsWith(BIOMETRIC_CONFIG_PREFIX) || parseBiometricConfigFromNotes(notes)) return true;
    const serviceName = String(svc?.service_name || '').toLowerCase();
    const planName = String(svc?.plan_name || '').toLowerCase();
    const billingName = String(svc?.billing_name || '').toLowerCase();
    return `${serviceName} ${planName} ${billingName}`.includes('biometric');
  };

  const isDigitalMarketingServiceRow = (svc) => {
    if (isIdCardServiceRow(svc) || isBiometricServiceRow(svc)) return false;
    const serviceName = String(svc?.service_name || '').toLowerCase();
    const planName = String(svc?.plan_name || '').toLowerCase();
    const notes = String(svc?.notes || '').toLowerCase();
    return (
      svc?.digitalservice != null ||
      notes.includes('auto digital order from agreement') ||
      serviceName.includes('digital') ||
      planName.includes('digital')
    );
  };

  const isErpServiceRow = (svc) => {
    if (isIdCardServiceRow(svc) || isBiometricServiceRow(svc) || isDigitalMarketingServiceRow(svc)) return false;
    const notes = String(svc?.notes || '').toLowerCase();
    if (notes.includes('bulk erp order') || notes.includes('auto erp order')) return true;
    return String(svc?.service_name || '').trim().toUpperCase() === 'ERP';
  };

  const resolveErpBillingType = (svc, payload) => {
    const source = payload || data;
    const notes = String(svc?.notes || '').toLowerCase();
    const qty = parseFloat(svc?.orderItemCount || svc?.item_count || 0) || 0;
    // Bulk ERP encode type in order qty (1 = fixed total, >1 = per student).
    if (notes.includes('bulk erp order')) {
      return qty > 1 ? 'per_student' : 'fixed';
    }
    const fromRow = String(svc?.erp_billing_type || '').toLowerCase();
    if (fromRow === 'fixed' || fromRow === 'per_student') return fromRow;
    const fromPayload = String(source?.erp_billing_type || '').toLowerCase();
    if (fromPayload === 'fixed' || fromPayload === 'per_student') return fromPayload;
    const entry = String(svc?.amount_entry || source?.amount_entry || '').trim();
    if (entry === 'Up to student') return 'fixed';
    if (entry === 'Number of students') return 'per_student';
    if (qty > 1) return 'per_student';
    if (qty === 1) return 'fixed';
    return 'per_student';
  };

  const isErpFixedBilling = (svc, payload) =>
    isErpServiceRow(svc) && resolveErpBillingType(svc, payload) === 'fixed';

  const erpBillingTypeLabel = (svc, payload) =>
    (resolveErpBillingType(svc, payload) === 'fixed' ? 'Fixed' : 'Per student');

  const serviceLineLabel = (svc) => {
    if (isIdCardServiceRow(svc)) return 'ID Cards';
    if (isBiometricServiceRow(svc)) {
      const cfg = parseBiometricConfigFromNotes(svc?.notes);
      if (cfg) return biometricKindLabel(cfg.kind);
      const name = String(svc?.service_name || svc?.plan_name || '').trim();
      if (name.toLowerCase().includes('manual')) return 'Manual Biometric';
      if (name.toLowerCase().includes('auto')) return 'Auto Biometric';
      return name || 'Biometric';
    }
    if (isDigitalMarketingServiceRow(svc)) {
      return String(svc?.service_name || svc?.plan_name || '').trim() || '-';
    }
    const serviceName = String(svc?.service_name || '').trim();
    const planName = String(svc?.plan_name || '').trim();
    const parts = [serviceName, planName].filter(Boolean);
    if (parts.length === 2 && parts[0].toLowerCase() === parts[1].toLowerCase()) {
      return parts[0];
    }
    return parts.length ? parts.join(' — ') : '-';
  };

  const isGpsServiceRow = (svc) => {
    if (isIdCardServiceRow(svc) || isBiometricServiceRow(svc)) return false;
    const serviceName = String(svc?.service_name || '').toLowerCase();
    const planName = String(svc?.plan_name || '').toLowerCase();
    const billingName = String(svc?.billing_name || '').toLowerCase();
    const blob = `${serviceName} ${planName} ${billingName}`;
    return blob.includes('gps');
  };

  const getOrderRemainingBase = (svc) => {
    const remaining = parseFloat(svc?.remainingBaseAmount);
    if (Number.isFinite(remaining) && remaining >= 0) return remaining;
    const unit = parseFloat(svc?.orderSvcAmount ?? svc?.service_amount ?? 0) || 0;
    const qty = parseFloat(svc?.orderItemCount ?? svc?.item_count ?? 1) || 0;
    return unit * qty;
  };

  const getCurrentIssueInvoiceId = () => {
    const id = new URLSearchParams(window.location.search).get('issue_invoice_id');
    return id ? Number(id) : null;
  };

  const getConsumedInvoiceItems = (svc) => {
    const excludeId = getCurrentIssueInvoiceId();
    const issueItems = Array.isArray(svc?.issue_invoice_items) ? svc.issue_invoice_items : [];
    const invItems = Array.isArray(svc?.invoice_items) ? svc.invoice_items : [];
    const items = issueItems.length ? issueItems : invItems;
    if (!excludeId || !issueItems.length) return items;
    return items.filter((it) => Number(it.issue_invoice ?? it.issue_invoice_id) !== excludeId);
  };

  const getPaymentTerms = (svc) => {
    const terms = Array.isArray(svc?.payment_terms) ? [...svc.payment_terms] : [];
    return terms.sort((a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0));
  };

  const getInvoicedTotalsFromItems = (svc) => {
    const invItems = getConsumedInvoiceItems(svc);
    let count = 0;
    let base = 0;
    let gross = 0;
    invItems.forEach((it) => {
      count += parseFloat(it.item_count || 0) || 0;
      const itemBase = parseFloat(it.total_without_tax || 0) || 0;
      const itemTax = parseFloat(it.tax_amount || 0) || 0;
      base += itemBase;
      gross += itemBase + itemTax;
    });
    if (svc.invoiced_total_item_count !== undefined && svc.invoiced_total_item_count !== null) {
      count = parseFloat(svc.invoiced_total_item_count || 0) || count;
    }
    return { count, base, gross };
  };

  const applyOrderQtyExpansion = (svc, newItemCount) => {
    const unit = parseFloat(svc.orderSvcAmount ?? svc.service_amount ?? 0) || 0;
    const gstPercent = parseFloat(svc.service_gst_percentage || 0) || 0;
    const { count: invoicedCount, base: invoicedBase } = getInvoicedTotalsFromItems(svc);
    const orderItemCount = parseFloat(svc.orderItemCount || 0) || 0;
    const itemCount = parseFloat(newItemCount || 0) || 0;
    // ERP / biometric invoice lines keep full student/device qty on every term invoice.
    // Do not treat prior invoice qty as consumed units (that double-counts the order).
    const amountBasedQty = isErpServiceRow(svc) || isBiometricServiceRow(svc);
    const effectiveOrderQty = amountBasedQty
      ? Math.max(orderItemCount, itemCount)
      : Math.max(orderItemCount, invoicedCount + itemCount);
    const newOrderBase = unit * effectiveOrderQty;
    const newOrderGst = gstPercent > 0 ? newOrderBase * gstPercent / 100 : 0;
    const remainingBase = Math.max(newOrderBase - invoicedBase, 0);
    const remainingGross = remainingBase + (gstPercent > 0 ? remainingBase * gstPercent / 100 : 0);

    if (effectiveOrderQty <= orderItemCount + 0.0001) {
      return {
        ...svc,
        remainingBaseAmount: remainingBase,
        remainingGrossAmount: remainingGross,
      };
    }

    return {
      ...svc,
      orderItemCount: effectiveOrderQty,
      order_base_snapshot: newOrderBase,
      order_gst_snapshot: newOrderGst,
      order_total_snapshot: newOrderBase + newOrderGst,
      orderTotal: newOrderBase + newOrderGst,
      remainingBaseAmount: remainingBase,
      remainingGrossAmount: remainingGross,
    };
  };

  const getServiceLineNet = (svc, idx) => {
    const disc = !isNaN(svc.discount_amount) ? Number(svc.discount_amount) : 0;
    const base = parseFloat(svc.service_base || 0) || 0;
    const gstPercent = isServiceGstChecked(svc, idx) ? (parseFloat(svc.service_gst_percentage || 0) || 0) : 0;
    const taxable = Math.max(base - disc, 0);
    return taxable + (taxable * gstPercent / 100);
  };

  const getServiceLineBalance = (svc, idx) => {
    const gstChecked = isServiceGstChecked(svc, idx);
    const { count: invoicedCount, base: invoicedBase, gross: invoicedGross } = getInvoicedTotalsFromItems(svc);
    const unit = parseFloat(svc.orderSvcAmount ?? svc.service_amount ?? 0) || 0;
    const orderQty = parseFloat(svc.orderItemCount || 0) || 0;
    const currentQty = parseFloat(svc.item_count || 0) || 0;
    const gstPercent = parseFloat(svc.service_gst_percentage || 0) || 0;
    const currentNet = getServiceLineNet(svc, idx);

    // ERP / biometric: billed by amount/terms. Each invoice line repeats full student/device
    // qty, so stacking invoicedCount + currentQty falsely inflates the order and Balance.
    if (isErpServiceRow(svc) || isBiometricServiceRow(svc)) {
      const snapshotBase = parseFloat(svc.order_base_snapshot || 0) || 0;
      const orderBase = snapshotBase > 0 ? snapshotBase : (unit * Math.max(orderQty, currentQty));
      const orderGross = orderBase + (gstChecked && gstPercent > 0 ? orderBase * gstPercent / 100 : 0);
      const referenceTotal = gstChecked ? orderGross - invoicedGross : orderBase - invoicedBase;
      return referenceTotal - currentNet;
    }

    const effectiveQty = Math.max(orderQty, invoicedCount + currentQty);
    const orderBase = unit * effectiveQty;
    const orderGross = orderBase + (gstChecked && gstPercent > 0 ? orderBase * gstPercent / 100 : 0);
    const referenceTotal = gstChecked ? orderGross - invoicedGross : orderBase - invoicedBase;
    return referenceTotal - currentNet;
  };

  /** ID card / biometric / ERP use qty × unit; others collapse to amount-as-unit. */
  const isQtyUnitServiceRow = (svc) =>
    isIdCardServiceRow(svc) || isBiometricServiceRow(svc) || isErpServiceRow(svc);
  const getMonthCountBetweenDates = (startDateStr, endDateStr) => {
    const start = startDateStr ? new Date(startDateStr) : null;
    const end = endDateStr ? new Date(endDateStr) : null;
    if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 1;
    if (end < start) return 1;
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
    return months > 0 ? months : 1;
  };
  const isWithoutGstOnlyOption = (svc) => String(svc?.payment_option || '').trim() === 'without_gst_invoice_acc';
  /** Other services (digital marketing lines) that can carry GST on issue invoices. */
  const isOtherServiceGstEligible = (svc) =>
    isDigitalMarketingServiceRow(svc) && !isWithoutGstOnlyOption(svc);
  const serviceKey = (svc, idx) => String(svc?.order || svc?.order_id || svc?.id || idx);
  const isServiceGstChecked = (svc, idx) => {
    if (isWithoutGstOnlyOption(svc)) return false;
    const key = serviceKey(svc, idx);
    return serviceGstSelection[key] !== false;
  };
  const effectiveGstPercent = (svc, idx) => (isServiceGstChecked(svc, idx) ? (parseFloat(svc.service_gst_percentage || 0) || 0) : 0);
  const getTermBasesForService = (svc) => {
    const orderBase = Number((parseFloat(svc?.order_base_snapshot || 0) || 0).toFixed(2));
    const terms = getPaymentTerms(svc);
    if (!terms.length) return [];
    if (orderBase <= 0) return terms.map(() => 0);

    const allocateByWeights = (weights) => {
      const weightSum = weights.reduce((sum, w) => sum + w, 0);
      if (weightSum <= 0) return null;
      let allocated = 0;
      return weights.map((weight, i) => {
        if (i === weights.length - 1) {
          return Number((orderBase - allocated).toFixed(2));
        }
        const amt = Number(((orderBase * weight) / weightSum).toFixed(2));
        allocated += amt;
        return amt;
      });
    };

    const pcts = terms.map((t) => parseFloat(t.amount_percentage || 0) || 0);
    const fromPct = allocateByWeights(pcts);
    if (fromPct) return fromPct;

    const rss = terms.map((t) => parseFloat(t.amount_rs || 0) || 0);
    const rsSum = rss.reduce((sum, v) => sum + v, 0);
    // ERP amount_rs often goes stale after qty/unit edits; prefer equal split when
    // stored rupee terms no longer match the current order base.
    if (isErpServiceRow(svc) && (rsSum <= 0.05 || Math.abs(rsSum - orderBase) > 0.05)) {
      const equal = allocateByWeights(terms.map(() => 1));
      if (equal) return equal;
    }
    const fromRs = allocateByWeights(rss);
    if (fromRs) return fromRs;

    return allocateByWeights(terms.map(() => 1)) || terms.map(() => 0);
  };

  const getTermBaseAmount = (svc, term, termIdx = null) => {
    const bases = getTermBasesForService(svc);
    if (termIdx != null && termIdx >= 0 && termIdx < bases.length) return bases[termIdx];
    const terms = getPaymentTerms(svc);
    const idx = terms.findIndex(
      (t) => t === term
        || (t?.id != null && term?.id != null && t.id === term.id)
        || (String(t?.term_name || '') === String(term?.term_name || '') && term?.term_name)
    );
    return idx >= 0 ? (bases[idx] || 0) : 0;
  };

  const getPendingTermsForService = (svc) => {
    // ID cards use item checkboxes (service + amount), not payment terms.
    if (isDigitalMarketingServiceRow(svc) || isIdCardServiceRow(svc)) return [];
    const terms = getPaymentTerms(svc);
    const termBases = getTermBasesForService(svc);
    let consumedBase = getConsumedInvoiceItems(svc).reduce(
      (sum, it) => sum + (parseFloat(it?.total_without_tax || 0) || 0),
      0
    );
    const out = [];
    terms.forEach((term, idx) => {
      const termBase = termBases[idx] || 0;
      const consumedInThisTerm = Math.max(0, Math.min(termBase, consumedBase));
      const pendingBase = Math.max(0, termBase - consumedInThisTerm);
      consumedBase = Math.max(0, consumedBase - termBase);
      out.push({
        idx,
        term_name: term?.term_name || `Term ${idx + 1}`,
        term_receive_date: term?.term_receive_date || '',
        termBase: Number(termBase.toFixed(2)),
        pendingBase: Number(pendingBase.toFixed(2)),
      });
    });
    return out;
  };

  /** Keep in-memory payment term rupee amounts aligned to current order base. */
  const syncPaymentTermsToOrderBase = (svc) => {
    const orderBase = Number((parseFloat(svc?.order_base_snapshot || 0) || 0).toFixed(2));
    const terms = Array.isArray(svc?.payment_terms) ? svc.payment_terms : [];
    if (!terms.length || orderBase <= 0) return svc;
    const bases = getTermBasesForService({ ...svc, payment_terms: terms });
    return {
      ...svc,
      payment_terms: terms.map((term, idx) => ({
        ...term,
        amount_rs: bases[idx] != null ? bases[idx] : term.amount_rs,
      })),
    };
  };

  /** ID card services/items for checkbox selection (label + amount). */
  const getIdCardItemsForService = (svc) => {
    if (!isIdCardServiceRow(svc)) return [];
    const cfg = parseIdCardConfigFromNotes(svc?.notes);
    const fromConfig = listIdCardConfigLineItems(cfg);
    if (fromConfig.length) {
      return fromConfig.map((line, idx) => ({
        idx,
        key: line.key,
        label: line.label,
        amount: line.amount,
        count: line.count,
      }));
    }
    // Agreement / non-config ID card: single selectable line
    const amount = Number(getOrderRemainingBase(svc).toFixed(2));
    if (amount <= 0) return [];
    return [{ idx: 0, key: 'idcard', label: 'ID Cards', amount }];
  };

  const getSelectedIdCardBase = (svc, idx) => {
    const items = getIdCardItemsForService(svc);
    if (!items.length) return 0;
    const key = serviceKey(svc, idx);
    const selectedSet = serviceTermSelection[key] || {};
    return Number(
      items.reduce((sum, item) => (selectedSet[item.idx] ? sum + (item.amount || 0) : sum), 0).toFixed(2)
    );
  };

  const getInclusiveYears = (fromDate, toDate) => {
    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;
    if (!from || !to || Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || to < from) return 1;
    let years = to.getFullYear() - from.getFullYear();
    if (to.getMonth() > from.getMonth() || (to.getMonth() === from.getMonth() && to.getDate() > from.getDate())) {
      years += 1;
    }
    return years > 0 ? years : 1;
  };

  const getPendingMonthsForService = (svc) => {
    if (!isDigitalMarketingServiceRow(svc)) return [];
    const invoicedCount = (Array.isArray(svc?.invoice_items) ? svc.invoice_items : []).reduce(
      (sum, it) => sum + (parseFloat(it?.item_count || 0) || 0),
      0
    );
    const unitAmount = parseFloat((svc?.orderSvcAmount ?? svc?.service_amount ?? 0)) || 0;
    const start = svc?.order_date ? new Date(svc.order_date) : null;
    const calcType = svc?.digital_calculation_type || svc?.calculation_type || 'monthwise';

    if (calcType === 'yearwise') {
      const totalYears = getInclusiveYears(svc?.order_date, svc?.service_end_date);
      const safeTotal = Number.isFinite(totalYears) && totalYears > 0 ? totalYears : 0;
      if (!safeTotal) return [];
      const paidYears = Math.max(0, Math.min(safeTotal, Math.floor(invoicedCount)));
      return Array.from({ length: Math.max(0, safeTotal - paidYears) }, (_, i) => {
        const yearIndex = paidYears + i;
        const d = start && !Number.isNaN(start.getTime())
          ? new Date(start.getFullYear() + yearIndex, start.getMonth(), start.getDate())
          : null;
        const label = d
          ? `${d.getFullYear()}-${d.getFullYear() + 1}`
          : `Year ${yearIndex + 1}`;
        return { idx: yearIndex, label, amount: unitAmount };
      });
    }

    if (calcType === 'studentcount') {
      const totalCount = parseFloat(svc?.orderItemCount || svc?.item_count || 1) || 1;
      if (invoicedCount >= totalCount) return [];
      const label = start
        ? start.toLocaleString('en-IN', { month: 'short', year: 'numeric' })
        : 'Student Count';
      return [{ idx: 0, label, amount: unitAmount * totalCount }];
    }

    const totalMonths = getMonthCountBetweenDates(svc?.order_date, svc?.service_end_date);
    const safeTotal = Number.isFinite(totalMonths) && totalMonths > 0 ? totalMonths : 0;
    if (!safeTotal) return [];
    const paidMonths = Math.max(0, Math.min(safeTotal, Math.floor(invoicedCount)));
    return Array.from({ length: Math.max(0, safeTotal - paidMonths) }, (_, i) => {
      const monthIndex = paidMonths + i;
      const d = start && !Number.isNaN(start.getTime())
        ? new Date(start.getFullYear(), start.getMonth() + monthIndex, 1)
        : null;
      const label = d
        ? d.toLocaleString('en-IN', { month: 'short', year: 'numeric' })
        : `Month ${monthIndex + 1}`;
      return { idx: monthIndex, label, amount: unitAmount };
    });
  };
  const getSelectedDigitalMonthCount = (svc, idx) => {
    const key = serviceKey(svc, idx);
    const selectedSet = serviceTermSelection[key] || {};
    const pendingMonths = getPendingMonthsForService(svc);
    return pendingMonths.reduce((sum, m) => (selectedSet[m.idx] ? sum + 1 : sum), 0);
  };

  const getSelectedDigitalItemCount = (svc, idx) => {
    const selectedCheckboxes = getSelectedDigitalMonthCount(svc, idx);
    const calcType = svc?.digital_calculation_type || svc?.calculation_type || 'monthwise';
    if (calcType === 'studentcount') {
      return selectedCheckboxes > 0 ? (parseFloat(svc?.orderItemCount || svc?.item_count || 1) || 1) : 0;
    }
    return selectedCheckboxes;
  };

  const getSelectedPendingBase = (svc, idx) => {
    if (isDigitalMarketingServiceRow(svc)) {
      const unit = parseFloat((svc?.orderSvcAmount ?? svc?.service_amount ?? 0)) || 0;
      const itemCount = getSelectedDigitalItemCount(svc, idx);
      return Number((unit * itemCount).toFixed(2));
    }
    if (isIdCardServiceRow(svc)) {
      return getSelectedIdCardBase(svc, idx);
    }
    const pendingTerms = getPendingTermsForService(svc);
    // Biometric with no payment terms: use remaining order amount
    if (pendingTerms.length === 0 && isBiometricServiceRow(svc)) {
      return Number(getOrderRemainingBase(svc).toFixed(2));
    }
    const key = serviceKey(svc, idx);
    const selectedSet = serviceTermSelection[key] || {};
    return pendingTerms.reduce((sum, t) => (selectedSet[t.idx] ? sum + (t.pendingBase || 0) : sum), 0);
  };
  const recalcServiceAmountsFromSelections = React.useCallback((srcData) => {
    const dataTemp = _.cloneDeep(srcData || {});
    if (!Array.isArray(dataTemp.pricing_services)) return dataTemp;
    dataTemp.pricing_services = dataTemp.pricing_services.map((svc, idx) => {
      if (isDigitalMarketingServiceRow(svc)) {
        const itemCount = getSelectedDigitalItemCount(svc, idx);
        const unit = parseFloat((svc?.orderSvcAmount ?? svc?.service_amount ?? 0)) || 0;
        const nextBase = itemCount > 0 ? unit * itemCount : 0;
        const gstChecked = isServiceGstChecked(svc, idx);
        const gstPercent = gstChecked ? (parseFloat(svc.service_gst_percentage || 0) || 0) : 0;
        const gstAmount = (nextBase * gstPercent) / 100;
        return {
          ...svc,
          item_count: itemCount,
          service_amount: Number(unit.toFixed(2)),
          service_base: Number(nextBase.toFixed(2)),
          service_gst: Number(gstAmount.toFixed(2)),
          service_total: Number((nextBase + gstAmount).toFixed(2)),
          discount_amount: parseFloat(svc.discount_amount || 0),
          auto_gst_discount: !gstChecked,
        };
      }
      const pendingBaseSelected = getSelectedPendingBase(svc, idx);
      const nextBase = pendingBaseSelected > 0 ? pendingBaseSelected : 0;
      const gstChecked = isServiceGstChecked(svc, idx);
      const gstPercent = parseFloat(svc.service_gst_percentage || 0) || 0;
      const effectiveGst = gstChecked ? gstPercent : 0;
      const gstAmount = (nextBase * effectiveGst) / 100;
      // ID card: invoice selected service lines as a single amount (no term-wise qty split)
      if (isIdCardServiceRow(svc)) {
        return {
          ...svc,
          item_count: nextBase > 0 ? 1 : 0,
          service_amount: Number(nextBase.toFixed(2)),
          service_base: Number(nextBase.toFixed(2)),
          service_gst: Number(gstAmount.toFixed(2)),
          service_total: Number((nextBase + gstAmount).toFixed(2)),
          discount_amount: parseFloat(svc.discount_amount || 0),
          auto_gst_discount: !gstChecked,
        };
      }
      if (isQtyUnitServiceRow(svc)) {
        // Term toggles must not change student/device qty or unit price — only the
        // invoiced base (selected term amount). Qty × unit can differ from base.
        const orderQty = parseFloat(svc?.orderItemCount || 0) || 0;
        const orderUnit = parseFloat(svc?.orderSvcAmount ?? 0) || 0;
        const curQty = parseFloat(svc?.item_count || 0) || 0;
        const curUnit = parseFloat(svc?.service_amount || 0) || 0;
        const qty = orderQty > 0 ? orderQty : curQty;
        const unit = orderUnit > 0 ? orderUnit : curUnit;
        const baseForGst = nextBase > 0 ? nextBase : 0;
        const gstForRow = (baseForGst * effectiveGst) / 100;
        return {
          ...svc,
          item_count: Number(qty.toFixed(2)),
          service_amount: Number(unit.toFixed(2)),
          service_base: Number(baseForGst.toFixed(2)),
          service_gst: Number(gstForRow.toFixed(2)),
          service_total: Number((baseForGst + gstForRow).toFixed(2)),
          discount_amount: parseFloat(svc.discount_amount || 0),
          auto_gst_discount: !gstChecked,
        };
      }
      return {
        ...svc,
        // Keep qty/unit fixed when terms change; only base follows selected terms.
        item_count: Number((
          (parseFloat(svc?.orderItemCount || 0) || 0) > 0
            ? (parseFloat(svc.orderItemCount) || 0)
            : (parseFloat(svc?.item_count || 0) || (nextBase > 0 ? 1 : 0))
        ).toFixed(2)),
        service_amount: Number((
          (parseFloat(svc?.orderSvcAmount || 0) || 0) > 0
            ? (parseFloat(svc.orderSvcAmount) || 0)
            : (parseFloat(svc?.service_amount || 0) || nextBase)
        ).toFixed(2)),
        service_base: Number(nextBase.toFixed(2)),
        service_gst: Number(gstAmount.toFixed(2)),
        service_total: Number((nextBase + gstAmount).toFixed(2)),
        // If GST is unchecked, invoice becomes without-GST (no auto discount).
        discount_amount: parseFloat(svc.discount_amount || 0),
        auto_gst_discount: !gstChecked,
      };
    });
    return dataTemp;
  }, [serviceTermSelection, serviceGstSelection]);

  /** ERP: per-student = student count × rate; fixed = qty 1 × total amount. */
  const resolveErpStudentQtyAndUnit = (svc, studentStrength, isFixed) => {
    const rawQty = parseFloat(svc?.item_count || 0) || 0;
    const rawUnit = parseFloat(svc?.service_amount || 0) || 0;
    const snapshotBase = parseFloat(svc?.order_base_snapshot || 0) || 0;
    const orderBase = snapshotBase > 0 ? snapshotBase : (rawQty * rawUnit);

    if (isFixed) {
      const qty = rawQty > 0 ? rawQty : 1;
      const unit = rawUnit > 0 ? rawUnit : orderBase;
      return {
        qty: Number(qty.toFixed(2)),
        unit: Number((unit || 0).toFixed(2)),
        orderBase: Number((orderBase > 0 ? orderBase : qty * (unit || 0)).toFixed(2)),
      };
    }

    // Already stored as per-student rate × student count
    if (rawQty > 1 && rawUnit > 0) {
      return {
        qty: Number(rawQty.toFixed(2)),
        unit: Number(rawUnit.toFixed(2)),
        orderBase: Number((rawQty * rawUnit).toFixed(2)),
      };
    }

    const students = parseFloat(studentStrength) || 0;
    if (students > 0 && orderBase > 0) {
      return {
        qty: Number(students.toFixed(2)),
        unit: Number((orderBase / students).toFixed(2)),
        orderBase: Number(orderBase.toFixed(2)),
      };
    }

    return {
      qty: Number((rawQty > 0 ? rawQty : 1).toFixed(2)),
      unit: Number((rawUnit > 0 ? rawUnit : orderBase).toFixed(2)),
      orderBase: Number((orderBase > 0 ? orderBase : rawQty * rawUnit).toFixed(2)),
    };
  };

  const buildEnrichedPayload = (payload) => {
    const studentStrength = parseFloat(payload?.student_strength) || 0;
    const updatedServices = (payload.pricing_services || []).map((svc) => {
      let gstPercent = parseFloat(svc.service_gst_percentage || 0) || 0;
      if (gstPercent <= 0 && isOtherServiceGstEligible(svc)) {
        gstPercent = DEFAULT_OTHER_SERVICE_GST_PERCENT;
      }
      let serviceAmount = parseFloat(svc.service_amount || 0) || 0;
      let itemCount = parseFloat(svc.item_count || 1) || 1;
      let originalBase = serviceAmount * itemCount;

      if (isErpServiceRow(svc)) {
        const resolved = resolveErpStudentQtyAndUnit(
          { ...svc, item_count: itemCount, service_amount: serviceAmount },
          studentStrength,
          resolveErpBillingType(svc, payload) === 'fixed'
        );
        itemCount = resolved.qty;
        serviceAmount = resolved.unit;
        originalBase = resolved.orderBase > 0 ? resolved.orderBase : (itemCount * serviceAmount);
      }

      const originalGst = gstPercent > 0 ? (originalBase * (gstPercent / 100)) : 0;
      const originalTotal = originalBase + originalGst;
      let invoicedCount = 0;
      let invoicedBaseAmount = 0;
      const invItems = getConsumedInvoiceItems(svc);
      for (const it of invItems) {
        invoicedCount += parseFloat(it.item_count || 0) || 0;
        invoicedBaseAmount += parseFloat(it.total_without_tax || 0) || 0;
      }
      const remainingBaseAmount = Math.max(originalBase - invoicedBaseAmount, 0);
      const remainingGrossAmount = remainingBaseAmount + (remainingBaseAmount * (gstPercent / 100));

      // ERP / biometric are billed by amount/terms — each prior invoice stores full
      // student/device qty, so remaining is amount-based. Keep qty + unit.
      if (isErpServiceRow(svc) || isBiometricServiceRow(svc)) {
        const isAllInvoiced = remainingBaseAmount <= 0.01;
        const gstAmount = gstPercent > 0 ? (remainingBaseAmount * (gstPercent / 100)) : 0;
        const enriched = {
          ...svc,
          service_gst_percentage: gstPercent,
          item_count: isAllInvoiced ? 0 : Number(itemCount.toFixed(2)),
          service_amount: Number(serviceAmount.toFixed(2)),
          order_base_snapshot: Number(originalBase.toFixed(2)),
          order_gst_snapshot: Number(originalGst.toFixed(2)),
          order_total_snapshot: Number(originalTotal.toFixed(2)),
          remainingBaseAmount,
          remainingGrossAmount,
          service_base: Number(remainingBaseAmount.toFixed(2)),
          service_gst: Number(gstAmount.toFixed(2)),
          service_total: Number((remainingBaseAmount + gstAmount).toFixed(2)),
          service_total_snapshot: Number((remainingBaseAmount + gstAmount).toFixed(2)),
          discount_amount: 0,
          toBeInvoicedAmt: remainingBaseAmount + gstAmount,
          orderTotal: Number(originalTotal.toFixed(2)),
          orderItemCount: Number(itemCount.toFixed(2)),
          orderBase: Number(remainingBaseAmount.toFixed(2)),
          orderGst: Number(gstAmount.toFixed(2)),
          orderSvcAmount: Number(serviceAmount.toFixed(2)),
          isAllInvoiced,
        };
        return syncPaymentTermsToOrderBase(enriched);
      }

      const remainingCount = itemCount - invoicedCount;
      const hasQtyRemaining = remainingCount > 0.0001;
      const hasAmountRemaining = remainingBaseAmount > 0.01;
      const isAllInvoiced = !hasQtyRemaining && !hasAmountRemaining;
      // After an existing invoice stores full qty (or qty was edited on that invoice),
      // remaining is amount-only. Do not collapse to qty=1 × remaining — that shrinks the order.
      const keepOrderMetrics = !hasQtyRemaining && hasAmountRemaining;
      const invoiceItemCount = isAllInvoiced
        ? 0
        : (keepOrderMetrics ? itemCount : remainingCount);
      const invoiceUnitAmount = serviceAmount;
      const baseAmount = keepOrderMetrics
        ? remainingBaseAmount
        : (invoiceUnitAmount * invoiceItemCount);
      const orderBaseAmt = serviceAmount * itemCount;
      const gstAmount = gstPercent > 0 ? (baseAmount * (gstPercent / 100)) : 0;
      const orderGstAmount = gstPercent > 0 ? (orderBaseAmt * (gstPercent / 100)) : 0;
      const totalOrderAmt = orderBaseAmt + orderGstAmount;
      const totalAmount = baseAmount + gstAmount;
      const toBeInvoicedAmt = _.cloneDeep(totalAmount);
      return {
        ...svc,
        service_gst_percentage: gstPercent,
        item_count: invoiceItemCount,
        service_amount: invoiceUnitAmount,
        order_base_snapshot: originalBase,
        order_gst_snapshot: originalGst,
        order_total_snapshot: originalTotal,
        remainingBaseAmount,
        remainingGrossAmount,
        service_base: baseAmount,
        service_gst: gstAmount,
        service_total: totalAmount,
        service_total_snapshot: totalAmount,
        discount_amount: 0,
        toBeInvoicedAmt,
        orderTotal: totalOrderAmt,
        orderItemCount: itemCount,
        orderBase: baseAmount,
        orderGst: gstAmount,
        orderSvcAmount: serviceAmount,
        isAllInvoiced,
      };
    });
    return { ...payload, pricing_services: updatedServices };
  };

  const mergeIssueInvoiceDetailIntoPayload = (detail, enrichedPayload) => {
    const byOrder = {};
    (detail.items || []).forEach((it) => {
      byOrder[Number(it.linked_order)] = it;
    });
    const pricing_services = (enrichedPayload.pricing_services || []).map((svc) => {
      const orderId = Number(svc.id);
      const it = byOrder[orderId];
      if (!it) return { ...svc };
      const item_count = parseFloat(it.item_count) || 0;
      const service_amount = parseFloat(it.amount) || 0;
      const service_gst_percentage = parseFloat(it.tax_percent) || 0;
      const discount_amount = parseFloat(it.discount_amount) || 0;
      const baseAmount = item_count * service_amount;
      const gstAmount = service_gst_percentage > 0 ? baseAmount * (service_gst_percentage / 100) : 0;
      // ERP display stays student count × per-student; invoice base comes from the saved line.
      // Saved issue lines may store term÷unit as qty (e.g. 52.63) — that is NOT student metrics.
      if (isErpServiceRow(svc)) {
        const isFixed = resolveErpBillingType(svc, enrichedPayload) === 'fixed';
        const strength = parseFloat(enrichedPayload?.student_strength) || 0;
        const orderQty = parseFloat(svc.orderItemCount || 0) || 0;
        const orderUnit = parseFloat(svc.orderSvcAmount || 0) || 0;
        const matchesOrderQty = orderQty > 1 && Math.abs(item_count - orderQty) <= 0.02;
        const matchesStrength = strength > 1 && Math.abs(item_count - strength) <= 0.02;
        const savedLooksLikeStudentMetrics = !isFixed && item_count > 1 && (matchesOrderQty || matchesStrength);
        let erpQty;
        let erpUnit;
        if (isFixed) {
          erpQty = orderQty > 0 ? orderQty : (item_count > 0 ? item_count : 1);
          erpUnit = orderUnit > 0 ? orderUnit : service_amount;
        } else if (savedLooksLikeStudentMetrics) {
          erpQty = item_count;
          erpUnit = service_amount > 0 ? service_amount : orderUnit;
        } else {
          const resolved = resolveErpStudentQtyAndUnit(
            {
              item_count: orderQty > 0 ? orderQty : item_count,
              service_amount: orderUnit > 0 ? orderUnit : service_amount,
              order_base_snapshot: parseFloat(svc.order_base_snapshot || 0) || 0,
            },
            strength,
            false,
          );
          erpQty = resolved.qty;
          erpUnit = resolved.unit;
        }
        // Prefer known order/student qty over a term-derived saved qty.
        if (!isFixed && orderQty > 1 && Math.abs(erpQty - orderQty) > 0.02) {
          erpQty = orderQty;
          if (orderUnit > 0) erpUnit = orderUnit;
        } else if (!isFixed && strength > 1 && Math.abs(erpQty - strength) > 0.02) {
          const resolved = resolveErpStudentQtyAndUnit(
            {
              item_count: orderQty > 1 ? orderQty : 1,
              service_amount: orderUnit > 0 ? orderUnit : (erpUnit || service_amount),
              order_base_snapshot: parseFloat(svc.order_base_snapshot || 0) || 0,
            },
            strength,
            false,
          );
          erpQty = resolved.qty;
          erpUnit = resolved.unit;
        }
        const savedBase = parseFloat(it.total_without_tax);
        const lineBase = Number.isFinite(savedBase) && savedBase > 0 ? savedBase : baseAmount;
        const lineGst = service_gst_percentage > 0
          ? lineBase * (service_gst_percentage / 100)
          : gstAmount;
        return {
          ...svc,
          item_count: erpQty,
          service_amount: erpUnit,
          orderItemCount: erpQty,
          orderSvcAmount: erpUnit,
          service_gst_percentage,
          discount_amount,
          notes: it.notes != null && it.notes !== '' ? it.notes : svc.notes,
          hsn_sac: it.hsn_sac != null && it.hsn_sac !== '' ? it.hsn_sac : svc.hsn_sac,
          service_base: lineBase,
          service_gst: lineGst,
          service_total: lineBase + lineGst,
          isAllInvoiced: false,
        };
      }
      return {
        ...svc,
        item_count,
        service_amount,
        service_gst_percentage,
        discount_amount,
        notes: it.notes != null && it.notes !== '' ? it.notes : svc.notes,
        hsn_sac: it.hsn_sac != null && it.hsn_sac !== '' ? it.hsn_sac : svc.hsn_sac,
        service_base: baseAmount,
        service_gst: gstAmount,
        service_total: baseAmount + gstAmount,
        isAllInvoiced: false,
      };
    });
    return { ...enrichedPayload, pricing_services };
  };

  const normalizeServiceRowsForDisplay = (payload) => {
    if (!Array.isArray(payload?.pricing_services)) return payload;
    return {
      ...payload,
      pricing_services: payload.pricing_services.map((svc) =>
        isQtyUnitServiceRow(svc) ? applyOrderQtyExpansion(svc, svc.item_count) : svc
      ),
    };
  };

  const initSelectionForEdit = (mergedPayload, detail) => {
    const ordersOnIssue = new Set((detail.items || []).map((it) => Number(it.linked_order)));
    const init = {};
    (mergedPayload.pricing_services || []).forEach((svc, idx) => {
      if (ordersOnIssue.has(Number(svc.id))) init[idx] = true;
    });
    return init;
  };

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const configId = params.get('config_id');
    const issueInvoiceId = params.get('issue_invoice_id');
    if (!configId) {
      setLoading(false);
      Swal.fire('Select company', 'Please choose a company before creating an issue invoice.', 'warning')
        .then(() => navigate('/billing/issue-invoice/list'));
      return;
    }
    setLoading(true);
    getRequest(`${GET_URL.billing_config.api}${configId}/`, {}, props)
      .then((response) => {
        const payload = response?.data?.data || response?.data;
        if (!payload) {
          setLoading(false);
          return;
        }
        const enriched = buildEnrichedPayload(payload);
        if (!issueInvoiceId) {
          setData(enriched);
          setLineItemsLocked(false);
          setLoading(false);
          return;
        }
        getRequest(`${GET_URL.issued_invoices.api}${issueInvoiceId}/`, {}, props)
          .then((r2) => {
            const detail = r2?.data?.data || r2?.data;
            if (!detail) {
              setData(enriched);
              setLoading(false);
              return;
            }
            const merged = normalizeServiceRowsForDisplay(
              mergeIssueInvoiceDetailIntoPayload(detail, enriched)
            );
            setData(merged);
            setSelectedServices(initSelectionForEdit(merged, detail));
            const issueDate = detail.issue_date || '';
            setInvoiceDetails({
              issue_date: issueDate,
              due_date: detail.due_date || (issueDate ? plusMonthsISO(issueDate, 1) : ''),
              status: detail.status || 'DRAFT',
              notes: detail.notes || '',
            });
            const st = String(detail.status || 'DRAFT').toUpperCase();
            setLineItemsLocked(['PARTIAL_PAID', 'PAID'].includes(st));
            setLoading(false);
          })
          .catch(() => {
            setData(enriched);
            setLineItemsLocked(false);
            setLoading(false);
          });
      })
      .catch(() => setLoading(false));
  }, [props.location && props.location.search]);

  // default: select all services once loaded (excluding fully invoiced ones); skip when editing an existing issue invoice (selection set from merge)
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('issue_invoice_id')) return;
    const svcs = data?.pricing_services || [];
    if (!Array.isArray(svcs) || svcs.length === 0) return;
    setSelectedServices((prev) => {
      if (Object.keys(prev || {}).length > 0) return prev;
      const init = {};
      svcs.forEach((svc, idx) => {
        if (!svc.isAllInvoiced) {
          init[idx] = true;
        }
      });
      return init;
    });
  }, [data?.pricing_services]);

  const selectedPricingServices = useMemo(() => {
    const svcs = Array.isArray(data?.pricing_services) ? data.pricing_services : []
    return svcs.filter((svc, idx) => !!selectedServices[idx] && !svc.isAllInvoiced)
  }, [data?.pricing_services, selectedServices])
  const isGstApplicable = useMemo(
    () => selectedPricingServices.some((svc, idx) => isServiceGstChecked(svc, idx)),
    [selectedPricingServices, serviceGstSelection]
  );

  const editableServices = useMemo(() => {
    return (data?.pricing_services || []).filter((svc) => !svc.isAllInvoiced)
  }, [data?.pricing_services])

  const termOptions = useMemo(() => {
    const map = new Map();
    editableServices.forEach((svc) => {
      if (isIdCardServiceRow(svc) || isDigitalMarketingServiceRow(svc)) return;
      const terms = getPaymentTerms(svc);
      terms.forEach((term) => {
        const key = String(term.term_name || '').trim();
        if (!key) return;
        if (!map.has(key)) {
          map.set(key, {
            term_name: key,
            term_receive_date: term.term_receive_date || '',
          });
        }
      });
    });
    return Array.from(map.values());
  }, [editableServices]);

  React.useEffect(() => {
    if (!selectedTermName) return;
    const exists = termOptions.some((t) => t.term_name === selectedTermName);
    if (!exists) {
      setSelectedTermName('');
    }
  }, [termOptions, selectedTermName]);

  const overallTotals = useMemo(()=>{
    return (selectedPricingServices || []).reduce((acc, svc) => {
      const base = parseFloat(svc.service_base || 0) || 0;
      const disc = parseFloat(svc.discount_amount || 0) || 0;
      const originalIdx = (data?.pricing_services || []).findIndex(
        (s) => (s.order || s.order_id || s.id) === (svc.order || svc.order_id || svc.id)
      );
      const gstPercent = originalIdx >= 0
        ? effectiveGstPercent(svc, originalIdx)
        : (parseFloat(svc.service_gst_percentage || 0) || 0);
      const taxable = Math.max(base - disc, 0);
      const gst = gstPercent > 0 ? (taxable * gstPercent / 100) : 0;
      acc.base += base;
      acc.gst += gst;
      acc.total += taxable + gst;
      return acc;
    }, { base: 0, gst: 0, total: 0 });
  }, [selectedPricingServices, data?.pricing_services, serviceGstSelection])

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('issue_invoice_id')) return;
    const today = new Date().toISOString().slice(0, 10);
    setInvoiceDetails((prev) => {
      const issueDate = prev.issue_date || today;
      return {
        ...prev,
        issue_date: issueDate,
        due_date: prev.due_date || plusMonthsISO(issueDate, 1),
      };
    });
  }, [overallTotals.base, overallTotals.gst, overallTotals.total]);

  React.useEffect(() => {
    const svcs = Array.isArray(data?.pricing_services) ? data.pricing_services : [];
    if (!svcs.length) return;
    const nextTerms = {};
    const nextGst = {};
    svcs.forEach((svc, idx) => {
      const key = serviceKey(svc, idx);
      const selected = {};
      if (isDigitalMarketingServiceRow(svc)) {
        const pendingMonths = getPendingMonthsForService(svc);
        pendingMonths.forEach((m) => {
          selected[m.idx] = true;
        });
      } else if (isIdCardServiceRow(svc)) {
        getIdCardItemsForService(svc).forEach((item) => {
          selected[item.idx] = true;
        });
      } else {
        const pendingTerms = getPendingTermsForService(svc);
        const isEditMode = Boolean(new URLSearchParams(window.location.search).get('issue_invoice_id'));
        if (isEditMode) {
          let remaining = parseFloat(svc.service_base || 0) || 0;
          pendingTerms.forEach((t) => {
            if (remaining > 0.05 && t.pendingBase > 0.001) {
              selected[t.idx] = true;
              remaining -= t.pendingBase;
            }
          });
          if (!Object.keys(selected).length && pendingTerms[0]) {
            selected[pendingTerms[0].idx] = true;
          }
        } else {
          const firstPending = pendingTerms.find((t) => t.pendingBase > 0.001) || pendingTerms[0];
          if (firstPending) selected[firstPending.idx] = true;
        }
      }
      nextTerms[key] = selected;
      if (isOtherServiceGstEligible(svc)) {
        const isEditMode = Boolean(new URLSearchParams(window.location.search).get('issue_invoice_id'));
        const savedGst = parseFloat(svc.service_gst_percentage || 0) || 0;
        nextGst[key] = isGpsServiceRow(svc)
          ? (isEditMode ? savedGst > 0 : false)
          : (isEditMode ? savedGst > 0 : true);
      } else {
        nextGst[key] = !isDigitalMarketingServiceRow(svc) && !isWithoutGstOnlyOption(svc);
      }
    });
    setServiceTermSelection(nextTerms);
    setServiceGstSelection(nextGst);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.pricing_services?.length]);

  React.useEffect(() => {
    setData((prev) => recalcServiceAmountsFromSelections(prev));
  }, [serviceTermSelection, serviceGstSelection, recalcServiceAmountsFromSelections]);

  const handleInvoiceFieldChange = (e) => {
    const { name, value } = e.target;
    if (name === 'issue_date' && value) {
      setInvoiceDetails((prev) => ({
        ...prev,
        issue_date: value,
        due_date: plusMonthsISO(value, 1),
      }));
      return;
    }
    setInvoiceDetails((prev) => ({ ...prev, [name]: value }));
  };

  const applyTermWiseToRows = (termName) => {
    const normalized = String(termName || '').trim();
    setSelectedTermName(normalized);
    if (!normalized) return;
    // Due date follows issue date (+1 month); term selection only updates line amounts.
    const dataTemp = _.cloneDeep(data);
    const nextSelected = {};

    (dataTemp.pricing_services || []).forEach((svc, idx) => {
      if (svc.isAllInvoiced) return;
      if (isDigitalMarketingServiceRow(svc) || isIdCardServiceRow(svc)) return;
      const terms = getPaymentTerms(svc);
      const picked = terms.find((t) => String(t.term_name || '').trim() === normalized);
      if (!picked) return;

      const nextBase = getTermBaseAmount(
        { ...svc, order_base_snapshot: svc.order_base_snapshot || (parseFloat(svc.service_amount || 0) * parseFloat(svc.orderItemCount || svc.item_count || 1)) },
        picked
      );
      if (isQtyUnitServiceRow(svc)) {
        // Keep qty and unit price; term only drives line base below.
        const orderQty = parseFloat(svc?.orderItemCount || 0) || 0;
        const orderUnit = parseFloat(svc?.orderSvcAmount ?? 0) || 0;
        const curQty = parseFloat(svc?.item_count || 0) || 0;
        const curUnit = parseFloat(svc?.service_amount || 0) || 0;
        svc.item_count = Number((orderQty > 0 ? orderQty : curQty).toFixed(2));
        svc.service_amount = Number((orderUnit > 0 ? orderUnit : curUnit).toFixed(2));
      } else {
        const orderQty = parseFloat(svc?.orderItemCount || 0) || 0;
        const orderUnit = parseFloat(svc?.orderSvcAmount ?? 0) || 0;
        const curQty = parseFloat(svc?.item_count || 0) || 0;
        const curUnit = parseFloat(svc?.service_amount || 0) || 0;
        svc.item_count = Number((orderQty > 0 ? orderQty : (curQty || 1)).toFixed(2));
        svc.service_amount = Number((orderUnit > 0 ? orderUnit : (curUnit || nextBase)).toFixed(2));
      }
      const gstPercent = effectiveGstPercent(svc, idx);
      svc.service_base = Number(nextBase.toFixed(2));
      svc.service_gst = Number(((nextBase * gstPercent) / 100).toFixed(2));
      svc.service_total = Number((svc.service_base + svc.service_gst).toFixed(2));
      // Do not auto-fill discount with GST when Add GST is off — just invoice without GST.
      if (!isServiceGstChecked(svc, idx) && svc.auto_gst_discount) {
        svc.discount_amount = 0;
        svc.auto_gst_discount = false;
      }
      nextSelected[idx] = true;
    });

    setData(dataTemp);
    setSelectedServices(nextSelected);
  };

  const onChangeVal = (e, idx) => {
    const { name, value, type } = e.target
    let val = value 
    const dataTemp = _.cloneDeep(data)

    // Auto-select row when any value changes
    setSelectedServices(prev => ({ ...(prev || {}), [idx]: true }));

    if (name !== 'notes' && name !== 'hsn_sac') {
      if (value) {
        val = Number(value)
      } else {
        val = 0
      }
      dataTemp.pricing_services[idx][name] = val
      const row = dataTemp.pricing_services[idx];
      const gstPercent = effectiveGstPercent(row, idx);
      const itemCount = isNaN(row.item_count) ? 1 : Number(row.item_count)
      const svcAmt = isNaN(row.service_amount) ? 0 : Number(row.service_amount)
      // Discount / GST % edits must keep term-selected base (often ≠ qty × unit for ERP).
      const qtyOrUnitChanged = name === 'item_count' || name === 'service_amount';
      const existingBase = parseFloat(row.service_base || 0) || 0;
      const baseAmount = qtyOrUnitChanged || existingBase <= 0
        ? (itemCount * svcAmt)
        : existingBase;
      const gstAmount = gstPercent > 0 ? (baseAmount * gstPercent / 100) : 0;
      dataTemp.pricing_services[idx].service_total = baseAmount + gstAmount
      dataTemp.pricing_services[idx].service_base = baseAmount
      dataTemp.pricing_services[idx].service_gst = gstAmount
      if (name === 'item_count' && isQtyUnitServiceRow(dataTemp.pricing_services[idx])) {
        dataTemp.pricing_services[idx] = applyOrderQtyExpansion(dataTemp.pricing_services[idx], val);
      }
    } else {
      dataTemp.pricing_services[idx][name] = val
    }
    setData(dataTemp)
  }

  const openQtyUnitDialog = (idx) => {
    const svc = data?.pricing_services?.[idx];
    if (!svc) return;
    setQtyUnitDialog({
      open: true,
      idx,
      item_count: svc.item_count ?? '',
      service_amount: svc.service_amount ?? '',
      isErp: isErpServiceRow(svc),
      isErpFixed: isErpFixedBilling(svc),
      errors: {},
    });
  };

  const closeQtyUnitDialog = () => {
    setQtyUnitDialog({
      open: false,
      idx: null,
      item_count: '',
      service_amount: '',
      isErp: false,
      isErpFixed: false,
      errors: {},
    });
  };

  const saveQtyUnitDialog = () => {
    const idx = qtyUnitDialog.idx;
    if (idx == null) return;
    const itemCount = parseFloat(qtyUnitDialog.item_count);
    const unitPrice = parseFloat(qtyUnitDialog.service_amount);
    const errors = {};
    if (!Number.isFinite(itemCount) || itemCount <= 0) {
      errors.item_count = qtyUnitDialog.isErp && !qtyUnitDialog.isErpFixed
        ? 'Student count must be greater than 0'
        : 'Qty must be greater than 0';
    }
    if (!Number.isFinite(unitPrice) || unitPrice <= 0) {
      errors.service_amount = qtyUnitDialog.isErp && !qtyUnitDialog.isErpFixed
        ? 'Per student amount must be greater than 0'
        : 'Unit price must be greater than 0';
    }
    if (Object.keys(errors).length) {
      setQtyUnitDialog((prev) => ({ ...prev, errors }));
      return;
    }

    setData((prev) => {
      const dataTemp = _.cloneDeep(prev || {});
      let svc = dataTemp.pricing_services?.[idx];
      if (!svc) return prev;

      svc.item_count = Number(itemCount.toFixed(2));
      svc.service_amount = Number(unitPrice.toFixed(2));
      // New baseline so term toggles keep these fixed values.
      svc.orderItemCount = svc.item_count;
      svc.orderSvcAmount = svc.service_amount;
      const orderBase = svc.item_count * svc.service_amount;
      const gstPercent = parseFloat(svc.service_gst_percentage || 0) || 0;
      const orderGst = gstPercent > 0 ? (orderBase * gstPercent) / 100 : 0;
      svc.order_base_snapshot = Number(orderBase.toFixed(2));
      svc.order_gst_snapshot = Number(orderGst.toFixed(2));
      svc.order_total_snapshot = Number((orderBase + orderGst).toFixed(2));
      svc.orderTotal = svc.order_total_snapshot;
      svc = syncPaymentTermsToOrderBase(svc);

      if (isQtyUnitServiceRow(svc)) {
        svc = applyOrderQtyExpansion(svc, svc.item_count);
        svc = syncPaymentTermsToOrderBase(svc);
        dataTemp.pricing_services[idx] = svc;
      }

      return recalcServiceAmountsFromSelections(dataTemp);
    });
    setSelectedServices((prev) => ({ ...(prev || {}), [idx]: true }));
    setServiceErrors((prev) => {
      const next = { ...prev };
      if (next[idx]) {
        delete next[idx].item_count;
        delete next[idx].service_amount;
      }
      return next;
    });
    closeQtyUnitDialog();
  };

  const onBlurValidate = (e, idx) => {
    const { name, value } = e.target
    const svc = data.pricing_services[idx]
    const errors = { ...serviceErrors }
    
    if (!errors[idx]) {
      errors[idx] = {}
    }

    if (name === 'item_count') {
      const itemCount = parseFloat(value || 0)

      if (isNaN(itemCount) || itemCount <= 0) {
        errors[idx].item_count = 'Item Count must be greater than 0'
      } else {
        delete errors[idx].item_count
      }
    } else if (name === 'service_gst_percentage') {
      if (!isServiceGstChecked(svc, idx)) {
        delete errors[idx].service_gst_percentage
        setServiceErrors(errors)
        return
      }
      const gstPercent = parseFloat(value || 0)
      if (isNaN(gstPercent) || gstPercent < 0) {
        errors[idx].service_gst_percentage = 'GST Percentage must be a valid number'
      } else if (gstPercent > 99) {
        errors[idx].service_gst_percentage = 'GST Percentage maximum is 99'
      } else {
        delete errors[idx].service_gst_percentage
      }
    } else if (name === 'service_amount') {
      const amt = parseFloat(value || 0)
      if (isNaN(amt) || amt < 0) {
        errors[idx].service_amount = 'Unit price must be a valid number'
      } else if (amt <= 0) {
        errors[idx].service_amount = 'Unit price must be greater than 0'
      } else {
        delete errors[idx].service_amount
      }
    } else if (name === 'discount_amount') {
      const discountAmt = parseFloat(value || 0)
      // Calculate service base (total excluding GST)
      const itemCount = parseFloat(svc.item_count || 0) || 0
      const svcAmt = parseFloat(svc.service_amount || 0) || 0
      const serviceBase = itemCount * svcAmt
      const maxDiscount = serviceBase

      if (isNaN(discountAmt) || discountAmt < 0) {
        errors[idx].discount_amount = 'Discount Amount must be a valid number'
      } else if (discountAmt > maxDiscount) {
        errors[idx].discount_amount = `Discount Amount cannot exceed ${maxDiscount.toFixed(2)} (Service Total excluding GST)`
      } else {
        delete errors[idx].discount_amount
      }
    }

    setServiceErrors(errors)
  }

  const validate = () => {
    const errors = {};
    const svcErrors = {};
    let isValid = true;

    if (lineItemsLocked) {
      if (!invoiceDetails.issue_date || invoiceDetails.issue_date.trim() === '') {
        errors.issue_date = 'Issue Date is required';
        isValid = false;
      }
      if (!invoiceDetails.due_date || invoiceDetails.due_date.trim() === '') {
        errors.due_date = 'Due Date is required';
        isValid = false;
      }
      if (invoiceDetails.issue_date && invoiceDetails.due_date) {
        const issueDate = new Date(invoiceDetails.issue_date);
        const dueDate = new Date(invoiceDetails.due_date);
        if (dueDate < issueDate) {
          errors.due_date = 'Due Date should be greater than or equal to Issue Date';
          isValid = false;
        }
      }
      setInvoiceErrors(errors);
      setServiceErrors({});
      return isValid;
    }

    // Validate invoice fields
    if (!invoiceDetails.issue_date || invoiceDetails.issue_date.trim() === '') {
      errors.issue_date = 'Issue Date is required';
      isValid = false;
    }
    if (!invoiceDetails.due_date || invoiceDetails.due_date.trim() === '') {
      errors.due_date = 'Due Date is required';
      isValid = false;
    }
    if (isEditRoute()) {
      if (!invoiceDetails.status || invoiceDetails.status.trim() === '') {
        errors.status = 'Status is required';
        isValid = false;
      }
    }
    
    // Validate issue_date <= MAX_DATE
    if (invoiceDetails.issue_date) {
      const issueDate = new Date(invoiceDetails.issue_date);
      const maxDate = new Date(MAX_DATE_STR);
      if (issueDate > maxDate) {
        errors.issue_date = `Issue Date should not be greater than ${MAX_DATE_STR}`;
        isValid = false;
      }
    }

    // Validate due_date >= issue_date
    if (invoiceDetails.issue_date && invoiceDetails.due_date) {
      const issueDate = new Date(invoiceDetails.issue_date);
      const dueDate = new Date(invoiceDetails.due_date);
      if (dueDate < issueDate) {
        errors.due_date = 'Due Date should be greater than or equal to Issue Date';
        isValid = false;
      }
    }
    
    // Validate issue_date >= order_date for all selected services
    if (invoiceDetails.issue_date && selectedPricingServices && selectedPricingServices.length > 0) {
      const issueDate = new Date(invoiceDetails.issue_date);
      for (const svc of selectedPricingServices) {
        if (svc.order_date) {
          const orderDate = new Date(svc.order_date);
          if (issueDate < orderDate) {
            errors.issue_date = `Issue Date should be greater than or equal to Service Order Order Date (${svc.order_date})`;
            isValid = false;
            break; // Show error for first violation
          }
        }
      }
    }

    // Validate at least one service is selected
    if (!selectedPricingServices || selectedPricingServices.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'At least one service must be selected to submit',
        timer: 2000,
        showConfirmButton: false
      });
      isValid = false;
    }

    // Validate service fields for selected services
    selectedPricingServices.forEach((svc, idx) => {
      const originalIdx = data.pricing_services.findIndex(s => 
        (s.order || s.order_id || s.id) === (svc.order || svc.order_id || svc.id)
      );
      if (originalIdx === -1) return;

      const svcError = {};

      // Validate item_count
      const itemCount = Number(svc.item_count || 0);
      if (isNaN(itemCount) || itemCount <= 0) {
        svcError.item_count = 'Item Count must be greater than 0';
        isValid = false;
      }

      const unitAmt = Number(svc.service_amount || 0);
      if (isNaN(unitAmt) || unitAmt <= 0) {
        svcError.service_amount = 'Unit price must be greater than 0';
        isValid = false;
      }

      // Validate service_gst_percentage
      if (isServiceGstChecked(svc, originalIdx)) {
        const gstPercent = Number(svc.service_gst_percentage || '');
        if (isNaN(gstPercent) || gstPercent === '' || gstPercent === null) {
          svcError.service_gst_percentage = 'GST Percentage is required';
          isValid = false;
        }
      }

      // Validate discount_amount
      const discountAmt = Number(svc.discount_amount || '');
      if (isNaN(discountAmt) || discountAmt === '' || discountAmt === null) {
        svcError.discount_amount = 'Discount Amount is required';
        isValid = false;
      }

      // If discount_amount > 0, notes is mandatory (config JSON notes do not count)
      const notesForDiscount = isConfigStoredNotes(svc.notes) ? (svc.line_notes || '') : (svc.notes || '');
      if (discountAmt > 0 && !svc.auto_gst_discount && (!notesForDiscount || notesForDiscount.trim() === '')) {
        svcError.notes = 'Notes is required when Discount Amount is greater than 0';
        isValid = false;
      }

      if (Object.keys(svcError).length > 0) {
        svcErrors[originalIdx] = svcError;
      }
    });

    setInvoiceErrors(errors);
    setServiceErrors(svcErrors);
    return isValid;
  };

  const submit = (redirectAfter = 'list') => {
    try {
      setPostSaveRedirect(redirectAfter);
      if (!validate()) {
        return;
      }

      const params = new URLSearchParams(window.location.search);
      const configId = params.get('config_id');
      const issueInvoiceId = params.get('issue_invoice_id');
      if (!configId) return;

      const items = selectedPricingServices.map((svc) => {
        const idx = data.pricing_services.findIndex(
          (s) => (s.order || s.order_id || s.id) === (svc.order || svc.order_id || svc.id),
        );
        const taxPercent =
          (!isServiceGstChecked(svc, idx))
            ? 0
            : parseFloat(svc.service_gst_percentage || 0);
        let itemCount = parseFloat(svc.item_count || 0);
        let amount = parseFloat(svc.service_amount || 0);
        const selectedBase = parseFloat(svc.service_base || 0) || 0;
        let lineTotalWithoutTax = null;

        const orderQty = parseFloat(svc.orderItemCount ?? svc.item_count ?? 0);
        const orderUnit = parseFloat(svc.orderSvcAmount ?? svc.service_amount ?? 0);
        const qtyTimesUnit = (Number.isFinite(itemCount) ? itemCount : 0) * (Number.isFinite(amount) ? amount : 0);

        // Keep order qty/unit; put the selected term/remaining amount on the line total.
        // Falling back to qty×unit after an existing invoice was edited would re-invoice
        // the full order and block (or overwrite) remaining invoices.
        if (isErpServiceRow(svc) || isBiometricServiceRow(svc) || Math.abs(qtyTimesUnit - selectedBase) > 0.02) {
          itemCount = orderQty;
          amount = orderUnit;
          lineTotalWithoutTax = selectedBase > 0.01 ? selectedBase : null;
        }

        const row = {
          order: svc.order || svc.order_id || svc.id,
          item_count: itemCount,
          amount,
          tax_percent: Number.isFinite(taxPercent) ? taxPercent : 0,
          hsn_sac: svc.hsn_sac || '',
          discount_amount: parseFloat(svc.discount_amount || 0),
          notes: isConfigStoredNotes(svc.notes)
            ? (svc.line_notes || '')
            : (svc.notes || ''),
        };
        if (lineTotalWithoutTax != null) {
          row.line_total_without_tax = lineTotalWithoutTax;
        }
        return row;
      }).filter((row) => {
        const line = row.line_total_without_tax != null
          ? parseFloat(row.line_total_without_tax)
          : (parseFloat(row.item_count || 0) * parseFloat(row.amount || 0));
        return row.order && parseFloat(row.item_count || 0) > 0 && line > 0.01;
      });
      if (!items.length) {
        Swal.fire(
          'No remaining amount',
          'Existing issue invoices already cover these services. Edit an existing invoice or wait until there is remaining amount to bill.',
          'warning',
        );
        return;
      }
      const payload = {
        billing_config: configId ? Number(configId) : undefined,
        issue_date: invoiceDetails.issue_date || '',
        due_date: invoiceDetails.due_date || '',
        status: issueInvoiceId ? (invoiceDetails.status || 'DRAFT') : 'DRAFT',
        is_gst_applicable: isGstApplicable,
        notes: invoiceDetails.notes || '',
        items,
      };

      setPendingPayload(payload)
      setSummaryData({
        company_name: data?.company_name,
        student_strength: data?.student_strength,
        pricing_services: selectedPricingServices.map((svc) => {
          const idx = data.pricing_services.findIndex(
            (s) => (s.order || s.order_id || s.id) === (svc.order || svc.order_id || svc.id),
          );
          const gstChecked = isServiceGstChecked(svc, idx);
          const gstPercent = gstChecked ? (parseFloat(svc.service_gst_percentage || 0) || 0) : 0;
          const idCardItems = isIdCardServiceRow(svc)
            ? getIdCardItemsForService(svc).filter((item) => {
                const key = serviceKey(svc, idx);
                return !!serviceTermSelection[key]?.[item.idx];
              })
            : [];
          return {
            ...svc,
            display_name: serviceLineLabel(svc),
            service_gst_percentage: gstPercent,
            notes: isConfigStoredNotes(svc.notes) ? (svc.line_notes || '') : (svc.notes || ''),
            selected_id_card_items: idCardItems,
          };
        }),
      })
      setSummaryOpen(true)
    } catch {
      // ignore
    }
  }

  const downloadIssueInvoicePdf = (invoiceId, invoiceNumber) => {
    if (!invoiceId) return Promise.resolve();
    const url = `${GET_URL.issued_invoices.api}${invoiceId}/`;
    const reqProps = { ...props, responseType: 'blob' };
    const requestParams = {
      generate_pdf: 1,
      show_gst: isGstApplicable ? 1 : 0,
      show_discount: 1,
      show_billing_summary: 0,
    };
    return getRequest(url, requestParams, reqProps)
      .then((response) => {
        if (!(response && response.status === 200)) return;
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const fileURL = window.URL.createObjectURL(blob);
        const contentDisposition =
          response?.headers?.['content-disposition'] || response?.headers?.['Content-Disposition'];
        const match = typeof contentDisposition === 'string'
          ? contentDisposition.match(/filename="?([^"]+)"?/i)
          : null;
        const filename = match?.[1] || `invoice_${invoiceNumber || invoiceId}.pdf`;
        const link = document.createElement('a');
        link.href = fileURL;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(fileURL);
        document.body.removeChild(link);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error downloading invoice PDF:', error);
        Swal.fire({
          icon: 'error',
          title: 'Download failed',
          text: 'Invoice was saved, but the PDF could not be downloaded.',
        });
      });
  };

  /**
   * Payments read GST invoices, which only exist once the issued invoice reaches Invoiced.
   * Status moves one step at a time (Draft → Issued → Invoiced).
   */
  const promoteToInvoiced = (issueInvoiceId, fromStatus) => {
    const steps = {
      DRAFT: ['ISSUED', 'INVOICED'],
      ISSUED: ['INVOICED'],
    }[String(fromStatus || 'DRAFT').toUpperCase()] || [];
    if (!issueInvoiceId || !steps.length) return Promise.resolve(true);
    const url = `${PATCH_URL.issued_invoices.api}${issueInvoiceId}/`;
    return steps.reduce(
      (chain, status) => chain.then((ok) => (
        ok
          ? patchRequest(url, { status }, props).then((r) => !!(r && r.status === 200))
          : false
      )),
      Promise.resolve(true),
    );
  };

  const confirmSubmit = () => {
    if (!pendingPayload) return;
    const issueInvoiceId = new URLSearchParams(window.location.search).get('issue_invoice_id');
    const statusBeforeSave = issueInvoiceId ? (invoiceDetails.status || 'DRAFT') : 'DRAFT';
    setSubmitDisable(true);
    const req = issueInvoiceId
      ? patchRequest(`${PATCH_URL.issued_invoices.api}${issueInvoiceId}/`, pendingPayload, props)
      : postRequest(POST_URL.issued_invoices.api, pendingPayload, {});
    req
      .then((response) => {
        setSummaryOpen(false);
        if (!(response && response.status === 200)) {
          setSubmitDisable(false);
          return;
        }
        const savedId = issueInvoiceId || response.data?.id;
        const invoiceNumber = response.data?.invoice_number;
        const goDownload = postSaveRedirect === 'download';
        const goPayment = postSaveRedirect === 'payment';

        const notify = (text, quick) => Swal.fire({
          position: 'top-end',
          type: 'success',
          title: 'Success',
          text,
          showConfirmButton: false,
          timer: quick ? 800 : 1500,
        });

        // Payments need the GST invoice, so advance the issued invoice to Invoiced first.
        if (goPayment) {
          promoteToInvoiced(savedId, statusBeforeSave).then((invoiced) => {
            setSubmitDisable(false);
            if (!invoiced || !data?.year || !data?.company) {
              notify(issueInvoiceId ? 'Invoice updated' : 'Invoice issued', false);
              navigate('/billing/issue-invoice/list');
              return;
            }
            notify('Invoice created. Opening payment…', true);
            navigate(`/billing/payment?year=${data.year}&company=${data.company}`);
          });
          return;
        }

        setSubmitDisable(false);
        if (goDownload && savedId) {
          notify(
            issueInvoiceId ? 'Invoice updated. Downloading PDF…' : 'Invoice issued. Downloading PDF…',
            true,
          );
          downloadIssueInvoicePdf(savedId, invoiceNumber)
            .finally(() => navigate('/billing/issue-invoice/list'));
          return;
        }
        notify(issueInvoiceId ? 'Invoice updated' : 'Invoice issued', false);
        navigate('/billing/issue-invoice/list');
      })
      .catch(() => setSubmitDisable(false));
  };

  const SectionHeading = ({ children, sx: sxProp = {} }) => (
    <Typography
      component="h2"
      variant="subtitle1"
      sx={{
        color: '#0a0a0a',
        fontWeight: 700,
        mb: 1.5,
        mt: 2,
        pb: 0.75,
        borderBottom: '1px solid',
        borderColor: 'divider',
        ...sxProp,
      }}
    >
      {children}
    </Typography>
  );
  const navigate = useNavigate(); 
  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, width: '100%', bgcolor: 'background.default' }}>
      <SummaryModal
        open={summaryOpen}
        onClose={() => setSummaryOpen(false)}
        onSubmit={confirmSubmit}
        data={summaryData}
        issueInvoiceSummary
        submitLabel={
          postSaveRedirect === 'download'
            ? (isEditRoute() ? 'Save and Download' : 'Submit and Download')
            : postSaveRedirect === 'payment'
              ? 'Invoice and Payment'
              : (isEditRoute() ? 'Save' : 'Submit')
        }
      />
      <Dialog open={qtyUnitDialog.open} onClose={closeQtyUnitDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {qtyUnitDialog.isErp
            ? (qtyUnitDialog.isErpFixed ? 'Edit fixed ERP amount' : 'Edit student count & per-student amount')
            : 'Edit qty & unit price'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label={qtyUnitDialog.isErp && !qtyUnitDialog.isErpFixed ? 'Student count (Qty)' : 'Qty'}
              type="number"
              size="small"
              fullWidth
              required
              value={qtyUnitDialog.item_count}
              onChange={(e) => setQtyUnitDialog((prev) => ({
                ...prev,
                item_count: e.target.value,
                errors: { ...prev.errors, item_count: undefined },
              }))}
              inputProps={{ min: 0.01, step: 0.01 }}
              error={!!qtyUnitDialog.errors?.item_count}
              helperText={
                qtyUnitDialog.errors?.item_count
                || (qtyUnitDialog.isErp
                  ? (qtyUnitDialog.isErpFixed ? 'Fixed billing uses qty 1' : 'Number of students')
                  : '')
              }
            />
            <TextField
              label={qtyUnitDialog.isErp && !qtyUnitDialog.isErpFixed ? 'Per student amount' : 'Unit price'}
              type="number"
              size="small"
              fullWidth
              required
              value={qtyUnitDialog.service_amount}
              onChange={(e) => setQtyUnitDialog((prev) => ({
                ...prev,
                service_amount: e.target.value,
                errors: { ...prev.errors, service_amount: undefined },
              }))}
              inputProps={{ min: 0.01, step: 0.01 }}
              error={!!qtyUnitDialog.errors?.service_amount}
              helperText={
                qtyUnitDialog.errors?.service_amount
                || (qtyUnitDialog.isErp
                  ? (qtyUnitDialog.isErpFixed ? 'Fixed ERP total (not per student)' : 'Amount for 1 student only')
                  : '')
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeQtyUnitDialog} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={saveQtyUnitDialog} sx={{ textTransform: 'none' }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      )}
      {!loading && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ color: '#0a0a0a' }}>
                {isEditRoute() ? 'Edit issue invoice' : 'Issue invoice'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                {lineItemsLocked
                  ? 'This invoice has payments recorded. It cannot be edited. Use Billing → Payments to adjust payments.'
                  : isEditRoute()
                    ? isInvoicedStatus()
                      ? 'Update dates, notes, and line amounts, then save. The linked GST invoice will be updated.'
                      : 'Update dates, status (Draft or Issued only), and line amounts, then save. Set Invoiced from Issued invoices to enable Payments.'
                    : 'Invoice creation'}
              </Typography>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap" alignSelf={{ xs: 'flex-start', sm: 'center' }}>
              <Button
                variant="contained"
                startIcon={<VisibilityOutlined />}
                onClick={() => {
                  if (navigate.length > 1) {
                    navigate(-1);
                  } else {
                    navigate('/billing/issue-invoice/list');
                  }
                }}
                sx={{ textTransform: 'none', borderRadius: 2, px: 2.5, py: 1 }}
              >
                View issued invoices
              </Button>
            </Box>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ color: '#0a0a0a', mb: 0.5 }}>
              {data.company_name ?? '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Taxable {formatAmount(overallTotals.base)}
              {' · '}
              GST {formatAmount(overallTotals.gst)}
              {' · '}
              Net {formatAmount(overallTotals.total)}
            </Typography>

            <SectionHeading sx={{ mt: 0 }}>Invoice</SectionHeading>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Issue Date"
                  type="date"
                  name="issue_date"
                  value={invoiceDetails.issue_date}
                  onChange={(e) => {
                    handleInvoiceFieldChange(e);
                    setInvoiceErrors(prev => ({ ...prev, issue_date: '' }));
                  }}
                  fullWidth
                  required
                  size="small"
                  disabled={lineItemsLocked}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: MAX_DATE_STR }}
                  error={!!invoiceErrors.issue_date}
                  helperText={invoiceErrors.issue_date || ''}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Due Date"
                  type="date"
                  name="due_date"
                  value={invoiceDetails.due_date}
                  onChange={(e) => {
                    handleInvoiceFieldChange(e);
                    setInvoiceErrors(prev => ({ ...prev, due_date: '' }));
                  }}
                  fullWidth
                  required
                  size="small"
                  disabled={lineItemsLocked}
                  InputLabelProps={{ shrink: true }}
                  error={!!invoiceErrors.due_date}
                  helperText={invoiceErrors.due_date || ''}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                {lineItemsLocked ? (
                  <TextField
                    label="Status"
                    value={statusLabel(invoiceDetails.status)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: true }}
                  />
                ) : isEditRoute() && isInvoicedStatus() ? (
                  <TextField
                    label="Status"
                    value={statusLabel(invoiceDetails.status)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: true }}
                  />
                ) : isEditRoute() ? (
                  <FormControl fullWidth size="small" error={!!invoiceErrors.status} required>
                    <InputLabel id="status-label">Status</InputLabel>
                    <Select
                      labelId="status-label"
                      label="Status"
                      name="status"
                      value={STATUS_CHOICES.includes(invoiceDetails.status) ? invoiceDetails.status : 'DRAFT'}
                      onChange={(e) => {
                        setInvoiceDetails(prev => ({ ...prev, status: e.target.value }));
                        setInvoiceErrors(prev => ({ ...prev, status: '' }));
                      }}
                    >
                      {STATUS_CHOICES.map(s => (
                        <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>
                      ))}
                    </Select>
                    {invoiceErrors.status && (
                      <Typography variant="caption" color="error" sx={{ ml: 1.75, mt: 0.5 }}>
                        {invoiceErrors.status}
                      </Typography>
                    )}
                  </FormControl>
                ) : (
                  <TextField
                    label="Status"
                    value="Draft"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: true }}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Invoice notes"
                  name="notes"
                  value={invoiceDetails.notes}
                  onChange={handleInvoiceFieldChange}
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  disabled={lineItemsLocked}
                />
              </Grid>
              {/* {!lineItemsLocked && termOptions.length > 0 && (
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="term-wise-label">Term-wise</InputLabel>
                    <Select
                      labelId="term-wise-label"
                      label="Term-wise"
                      value={selectedTermName}
                      onChange={(e) => applyTermWiseToRows(e.target.value)}
                    >
                      <MenuItem value=""> 
                        <em>Select term</em>
                      </MenuItem>
                      {termOptions.map((term) => (
                        <MenuItem key={term.term_name} value={term.term_name}>
                          {term.term_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )} */}
            </Grid>

            <SectionHeading sx={{ mt: 0 }}>Line items</SectionHeading>
            {editableServices.length === 0 ? (
              <Typography variant="body2" color="textSecondary">No services available for issue invoice</Typography>
            ) : (
              <Table size="small" sx={{ '& .MuiTableCell-head': { bgcolor: 'grey.100', fontWeight: 700 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        disabled={lineItemsLocked}
                        checked={selectedPricingServices.length > 0 && selectedPricingServices.length === editableServices.length}
                        indeterminate={selectedPricingServices.length > 0 && selectedPricingServices.length < editableServices.length}
                        onChange={(e) => {
                          const checked = e.target.checked
                          const init = {}
                          data.pricing_services.forEach((svc, idx) => {
                            if (!svc.isAllInvoiced) {
                              init[idx] = checked
                            }
                          })
                          setSelectedServices(prev => ({ ...prev, ...init }))
                        }}
                      />
                    </TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell style={{ minWidth: 88 }}>Qty</TableCell>
                    <TableCell align="right" style={{ minWidth: 120 }}>Unit price</TableCell>
                    <TableCell align="right" style={{ minWidth: 88 }}>GST %</TableCell>
                    <TableCell style={{ minWidth: 220 }}>Terms</TableCell>
                    <TableCell align="right" style={{ minWidth: 120 }}>Total amount</TableCell>
                    <TableCell align="right">Gross</TableCell>
                    <TableCell align="right" style={{ minWidth: 100 }}>Discount</TableCell>
                    <TableCell align="right">Net</TableCell>
                    <TableCell align="right" style={{ minWidth: 110 }}>Balance</TableCell>
                    <TableCell style={{ minWidth: 100 }}>HSN/SAC</TableCell>
                    <TableCell style={{ minWidth: 140 }}>Line notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {editableServices.map((svc) => {
                  const originalIdx = data.pricing_services.findIndex(s => (s.order || s.order_id || s.id) === (svc.order || svc.order_id || svc.id))
                  const disc = !isNaN(svc.discount_amount) ? Number(svc.discount_amount) : 0
                  return (
                    <TableRow key={`svc-${originalIdx}`}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            disabled={lineItemsLocked}
                            checked={!!selectedServices[originalIdx]}
                            onChange={(e) => setSelectedServices(prev => ({ ...(prev || {}), [originalIdx]: e.target.checked }))}
                          />
                        </TableCell>
                        <TableCell>
                          {serviceLineLabel(svc)}
                          {isErpServiceRow(svc) && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Billing: {erpBillingTypeLabel(svc)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ minWidth: 40 }}>
                              {svc.item_count ?? '—'}
                            </Typography>
                            {!lineItemsLocked
                              && !isDigitalMarketingServiceRow(svc)
                              && !isIdCardServiceRow(svc) && (
                              <IconButton
                                size="small"
                                aria-label="Edit qty and unit price"
                                onClick={() => openQtyUnitDialog(originalIdx)}
                              >
                                <EditOutlined fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                          {!!serviceErrors[originalIdx]?.item_count && (
                            <Typography variant="caption" color="error" display="block">
                              {serviceErrors[originalIdx].item_count}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatAmount(svc.service_amount)}
                          </Typography>
                          {!!serviceErrors[originalIdx]?.service_amount && (
                            <Typography variant="caption" color="error" display="block">
                              {serviceErrors[originalIdx].service_amount}
                            </Typography>
                          )}
                          {!serviceErrors[originalIdx]?.service_amount && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {isErpServiceRow(svc) && !isIdCardServiceRow(svc) && !isBiometricServiceRow(svc)
                                ? (resolveErpBillingType(svc) === 'fixed'
                                  ? 'Fixed amount'
                                  : (data?.student_strength != null
                                    ? `Per student · strength ${data.student_strength}`
                                    : 'Per student amount'))
                                : isBiometricServiceRow(svc)
                                  ? 'Qty = devices, unit = fee per device'
                                  : isIdCardServiceRow(svc)
                                    ? 'Sum of selected ID card services'
                                    : ''}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            disabled={lineItemsLocked}
                            size="small"
                            name="service_gst_percentage"
                            required
                            type="number"
                            inputProps={{ min: 0, max: 99, step: 0.01 }}
                            value={(isServiceGstChecked(svc, originalIdx) ? svc.service_gst_percentage : 0)}
                            onChange={(e) => {
                              if (!isServiceGstChecked(svc, originalIdx)) return;
                              onChangeVal(e, originalIdx);
                              setServiceErrors(prev => {
                                const newErrors = { ...prev };
                                if (newErrors[originalIdx]) {
                                  delete newErrors[originalIdx].service_gst_percentage;
                                }
                                return newErrors;
                              });
                            }}
                            onBlur={(e) => onBlurValidate(e, originalIdx)}
                            InputProps={{ readOnly: !isServiceGstChecked(svc, originalIdx) }}
                            error={!!serviceErrors[originalIdx]?.service_gst_percentage}
                            helperText={(serviceErrors[originalIdx]?.service_gst_percentage || '')}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {isOtherServiceGstEligible(svc) && (
                              <FormControlLabel
                                sx={{ m: 0 }}
                                control={(
                                  <Checkbox
                                    size="small"
                                    checked={isServiceGstChecked(svc, originalIdx)}
                                    onChange={(e) => {
                                      const key = serviceKey(svc, originalIdx);
                                      if (isWithoutGstOnlyOption(svc)) return;
                                      const checked = e.target.checked;
                                      setServiceGstSelection((prev) => ({ ...prev, [key]: checked }));
                                      if (checked) {
                                        const pct = parseFloat(svc.service_gst_percentage || 0) || 0;
                                        if (pct <= 0 && isOtherServiceGstEligible(svc)) {
                                          onChangeVal(
                                            { target: { name: 'service_gst_percentage', value: String(DEFAULT_OTHER_SERVICE_GST_PERCENT) } },
                                            originalIdx,
                                          );
                                        }
                                      }
                                      setSelectedServices((prev) => ({ ...prev, [originalIdx]: true }));
                                    }}
                                    disabled={isWithoutGstOnlyOption(svc)}
                                  />
                                )}
                                label={<Typography variant="caption">Add GST</Typography>}
                              />
                            )}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                              {isDigitalMarketingServiceRow(svc) ? (
                                getPendingMonthsForService(svc).map((m) => {
                                  const key = serviceKey(svc, originalIdx);
                                  const checked = !!serviceTermSelection[key]?.[m.idx];
                                  return (
                                    <FormControlLabel
                                      key={`${key}-m-${m.idx}`}
                                      sx={{ m: 0 }}
                                      control={(
                                        <Checkbox
                                          size="small"
                                          checked={checked}
                                          disabled={lineItemsLocked}
                                          onChange={(e) => {
                                            const isChecked = e.target.checked;
                                            setServiceTermSelection((prev) => ({
                                              ...prev,
                                              [key]: { ...(prev[key] || {}), [m.idx]: isChecked },
                                            }));
                                            setSelectedServices((prev) => ({ ...prev, [originalIdx]: true }));
                                          }}
                                        />
                                      )}
                                      label={(
                                        <Typography variant="caption" color="text.secondary">
                                          {m.label} ({formatAmount(m.amount)})
                                        </Typography>
                                      )}
                                    />
                                  );
                                })
                              ) : isIdCardServiceRow(svc) ? (
                                getIdCardItemsForService(svc).map((item) => {
                                  const key = serviceKey(svc, originalIdx);
                                  const checked = !!serviceTermSelection[key]?.[item.idx];
                                  return (
                                    <FormControlLabel
                                      key={`${key}-id-${item.key}`}
                                      sx={{ m: 0 }}
                                      control={(
                                        <Checkbox
                                          size="small"
                                          checked={checked}
                                          disabled={lineItemsLocked}
                                          onChange={(e) => {
                                            const isChecked = e.target.checked;
                                            setServiceTermSelection((prev) => ({
                                              ...prev,
                                              [key]: { ...(prev[key] || {}), [item.idx]: isChecked },
                                            }));
                                            setSelectedServices((prev) => ({ ...prev, [originalIdx]: true }));
                                          }}
                                        />
                                      )}
                                      label={(
                                        <Typography variant="caption">
                                          {item.label}
                                          {item.count > 0 ? ` — Qty: ${item.count}` : ''}
                                          {' '}({formatAmount(item.amount)})
                                        </Typography>
                                      )}
                                    />
                                  );
                                })
                              ) : getPendingTermsForService(svc).length > 0 ? (
                                getPendingTermsForService(svc).map((t) => {
                                const key = serviceKey(svc, originalIdx);
                                const checked = !!serviceTermSelection[key]?.[t.idx];
                                const disabled = t.pendingBase <= 0.001;
                                return (
                                  <FormControlLabel
                                    key={`${key}-t-${t.idx}`}
                                    sx={{ m: 0 }}
                                    control={(
                                      <Checkbox
                                        size="small"
                                        checked={checked}
                                        disabled={disabled}
                                        onChange={(e) => {
                                          const isChecked = e.target.checked;
                                          setServiceTermSelection((prev) => ({
                                            ...prev,
                                            [key]: { ...(prev[key] || {}), [t.idx]: isChecked },
                                          }));
                                          setSelectedServices((prev) => ({ ...prev, [originalIdx]: true }));
                                        }}
                                      />
                                    )}
                                    label={(
                                      <Typography variant="caption">
                                        {t.term_name} ({formatAmount(t.pendingBase)})
                                      </Typography>
                                    )}
                                  />
                                );
                              })
                              ) : isBiometricServiceRow(svc) ? (
                                <Typography variant="caption" color="text.secondary">
                                  Full remaining amount ({formatAmount(getOrderRemainingBase(svc))})
                                </Typography>
                              ) : null}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {(() => {
                            const withGstTotal = parseFloat(
                              svc.order_total_snapshot ??
                                svc.orderTotal ??
                                svc.order_total_amount ??
                                0
                            ) || 0;
                            const baseOnly = parseFloat(svc.order_base_snapshot ?? 0) || 0;
                            const show = isServiceGstChecked(svc, originalIdx) ? withGstTotal : baseOnly;
                            return formatAmount(show);
                          })()}
                        </TableCell>
                        <TableCell align="right">{formatAmount(svc.service_total)}</TableCell>
                        <TableCell align="right">
                          <TextField
                            disabled={lineItemsLocked}
                            size="small"
                            name="discount_amount"
                            required
                            type="number"
                            inputProps={{ min: 0, step: 0.01 }}
                            value={svc.discount_amount}
                            onChange={(e) => {
                              onChangeVal(e, originalIdx);
                              setServiceErrors(prev => {
                                const newErrors = { ...prev };
                                if (newErrors[originalIdx]) {
                                  delete newErrors[originalIdx].discount_amount;
                                  const discountAmt = parseFloat(e.target.value || 0);
                                  if (discountAmt <= 0) {
                                    delete newErrors[originalIdx].notes;
                                  }
                                }
                                return newErrors;
                              });
                            }}
                            onBlur={(e) => onBlurValidate(e, originalIdx)}
                            error={!!serviceErrors[originalIdx]?.discount_amount}
                            helperText={serviceErrors[originalIdx]?.discount_amount || ''}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {(() => {
                            const base = parseFloat(svc.service_base || 0) || 0;
                            const gstPercent = isServiceGstChecked(svc, originalIdx) ? (parseFloat(svc.service_gst_percentage || 0) || 0) : 0;
                            const taxable = Math.max(base - disc, 0);
                            const net = taxable + (taxable * gstPercent / 100);
                            return formatAmount(net);
                          })()}
                        </TableCell>
                        <TableCell align="right">
                          {formatAmount(getServiceLineBalance(svc, originalIdx))}
                        </TableCell>
                        <TableCell>
                          <TextField
                            disabled={lineItemsLocked}
                            size="small"
                            name="hsn_sac"
                            value={svc.hsn_sac}
                            onChange={(e) => onChangeVal(e, originalIdx)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            disabled={lineItemsLocked}
                            size="small"
                            name="notes"
                            value={
                              isConfigStoredNotes(svc.notes)
                                ? (svc.line_notes || '')
                                : (svc.notes || '')
                            }
                            placeholder={
                              isIdCardServiceRow(svc)
                                ? 'ID card line notes (optional)'
                                : isBiometricServiceRow(svc)
                                  ? 'Biometric line notes (optional)'
                                  : ''
                            }
                            multiline
                            onChange={(e) => {
                              const val = e.target.value;
                              const dataTemp = _.cloneDeep(data);
                              const row = dataTemp.pricing_services[originalIdx];
                              if (isConfigStoredNotes(row.notes)) {
                                row.line_notes = val;
                              } else {
                                row.notes = val;
                              }
                              setData(dataTemp);
                              setSelectedServices((prev) => ({ ...(prev || {}), [originalIdx]: true }));
                              setServiceErrors((prev) => {
                                const newErrors = { ...prev };
                                if (newErrors[originalIdx]) {
                                  delete newErrors[originalIdx].notes;
                                }
                                return newErrors;
                              });
                            }}
                            error={!!serviceErrors[originalIdx]?.notes}
                            helperText={
                              serviceErrors[originalIdx]?.notes ||
                              (isConfigStoredNotes(svc.notes)
                                ? (isIdCardServiceRow(svc) ? 'Configured on order' : 'Configured on order')
                                : '')
                            }
                          />
                        </TableCell>
                      </TableRow>
                  )})}
                </TableBody>
              </Table>
            )}
            {!lineItemsLocked && (
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">
                  Taxable: <b>{formatAmount(overallTotals.base)}</b>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  GST: <b>{formatAmount(overallTotals.gst)}</b>
                </Typography>
                <Typography variant="body2" color="text.primary">
                  Net total: <b>{formatAmount(overallTotals.total)}</b>
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => submit('payment')}
                disabled={submitDisable}
                sx={{ textTransform: 'none', borderRadius: 2, px: 3, py: 1 }}
              >
                Invoice and Payment
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<DownloadOutlined />}
                onClick={() => submit('download')}
                disabled={submitDisable}
                sx={{ textTransform: 'none', borderRadius: 2, px: 3, py: 1 }}
              >
                {isEditRoute() ? 'Save and Download' : 'Submit and Download'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => submit()}
                disabled={submitDisable}
                sx={{ textTransform: 'none', borderRadius: 2, px: 3, py: 1 }}
              >
                {isEditRoute() ? 'Save' : 'Submit'}
              </Button>
              </Box>
            </Box>
            )}
          </Paper>
        </>
      )}

    </Box>
  );
}

export default CompanyIssueInvoice;
